import { create } from 'zustand';
import type { PublicKey, WSCallHistoryEvent } from '@shared/types';
import {
  clearCallHistory as clearCallHistoryStorage,
  getCallHistoryEvents,
  initializeCallHistoryFromLegacyStorage,
  markMissedCallEventsSeen,
  putCallEvent,
  upsertCallEventBySessionId,
  type StoredCallEvent,
  updateCallEventByCallSessionId
} from '../callHistory/storage';
import { clearNativeMissedCallNotifications, clearNativeMissedCallerBadges } from '../utils/webviewBridge';
import { useAuthStore } from './authStore';

// Кэш ключей звонящих, полученных во время E2EE верификации.
// Он сохраняет ключ до первой синхронизации истории и поддерживает старые серверы.
const pendingRemoteKeys = new Map<string, PublicKey>();

export function setPendingCallRemoteKey(callSessionId: string, key: PublicKey): void {
  const cleanCallSessionId = (callSessionId || '').trim();
  if (!cleanCallSessionId || !key?.value) return;

  pendingRemoteKeys.set(cleanCallSessionId, key);
  void useCallHistoryStore.getState().updateByCallSessionId(cleanCallSessionId, {
    remotePublicKey: key
  });
}

function isIncomingMissedEvent(event: Pick<CallEvent, 'direction' | 'status' | 'durationSeconds'>): boolean {
  return event.direction === 'incoming'
    && (
      event.status === 'failed'
      || (event.status === 'ended' && !event.durationSeconds)
    );
}

export interface CallEvent {
  id: string;
  callSessionId?: string;
  direction: 'incoming' | 'outgoing';
  status: 'ringing' | 'answered' | 'rejected' | 'ended' | 'failed';
  localIdentityId?: string;
  remoteIdentityId: string;
  remoteDisplayName: string;
  remoteIdentityName?: string;
  remotePublicKey?: PublicKey;
  isKnownContact: boolean;
  e2eeVerified: boolean;
  serverId: string;
  serverName: string;
  serverUrl?: string;
  isTemporaryLinkCall?: boolean;
  timestamp: string;
  createdAt?: string;
  connectedAt?: string;
  endedAt?: string;
  reason?: string;
  lastUpdatedAt?: string;
  durationSeconds?: number;
  seen?: boolean;
}

interface CallHistoryState {
  events: CallEvent[];
  initialized: boolean;
  initialize: () => Promise<void>;
  refresh: () => Promise<void>;
  addEvent: (event: Omit<CallEvent, 'id' | 'timestamp'> & { timestamp?: string }) => Promise<void>;
  upsertServerEvent: (event: WSCallHistoryEvent & {
    serverId?: string;
    serverName?: string;
    serverUrl?: string;
  }) => Promise<void>;
  updateByCallSessionId: (
    callSessionId: string,
    updates: Partial<Omit<CallEvent, 'id' | 'timestamp' | 'callSessionId'>>
  ) => Promise<void>;
  markMissedCallsSeen: (filters?: { serverId?: string; identityId?: string; peerIdentityId?: string }) => Promise<void>;
  clearHistory: () => Promise<void>;
}

function sortNewestFirst(events: StoredCallEvent[]): CallEvent[] {
  return [...events].sort((a, b) => {
    const tsDiff = new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    if (tsDiff !== 0) return tsDiff;
    return b.id.localeCompare(a.id);
  });
}

async function loadPersistedEvents(): Promise<CallEvent[]> {
  const existing = await getCallHistoryEvents();
  if (existing.length > 0) return sortNewestFirst(existing);
  const migrated = await initializeCallHistoryFromLegacyStorage();
  return sortNewestFirst(migrated);
}

export const useCallHistoryStore = create<CallHistoryState>((set, get) => ({
  events: [],
  initialized: false,

  initialize: async () => {
    if (get().initialized) return;
    const events = await loadPersistedEvents();
    set({ events, initialized: true });
  },

  refresh: async () => {
    const events = await loadPersistedEvents();
    set({ events, initialized: true });
  },

  addEvent: async (eventData) => {
    const providedTimestamp = eventData.timestamp;
    const isIncomingMissed = isIncomingMissedEvent(eventData);

    const newEvent: CallEvent = {
      ...eventData,
      id: `${Date.now()}-${Math.random()}`,
      timestamp: providedTimestamp || new Date().toISOString(),
      seen:
        typeof eventData.seen === 'boolean'
          ? eventData.seen
          : isIncomingMissed
            ? false
            : true
    };

    set((state) => ({
      events: sortNewestFirst([...state.events, newEvent])
    }));

    await putCallEvent(newEvent);
    window.dispatchEvent(new Event('call:history:updated'));
  },

  upsertServerEvent: async (eventData) => {
    const timestamp = new Date(eventData.timestamp).toISOString();
    const createdAt = new Date(eventData.createdAt).toISOString();
    const connectedAt = typeof eventData.connectedAt === 'number' ? new Date(eventData.connectedAt).toISOString() : undefined;
    const endedAt = typeof eventData.endedAt === 'number' ? new Date(eventData.endedAt).toISOString() : undefined;
    const lastUpdatedAt = new Date(eventData.lastUpdatedAt).toISOString();
    const isIncomingMissed = eventData.direction === 'incoming'
      && (
        eventData.status === 'failed'
        || (eventData.status === 'ended' && !eventData.durationSeconds)
      );
    const serverSeen = typeof eventData.missedSeenAt === 'number' && eventData.missedSeenAt > 0;

    const pendingKey = eventData.callSessionId ? pendingRemoteKeys.get(eventData.callSessionId) : undefined;

    const nextEvent: CallEvent = {
      id: `server:${eventData.callSessionId}`,
      callSessionId: eventData.callSessionId,
      direction: eventData.direction,
      status: eventData.status,
      localIdentityId: eventData.localIdentityId,
      remoteIdentityId: eventData.remoteIdentityId,
      remoteDisplayName: eventData.remoteIdentityName || eventData.remoteIdentityId,
      remoteIdentityName: eventData.remoteIdentityName,
      remotePublicKey: eventData.remotePublicKey ?? pendingKey,
      isKnownContact: false,
      e2eeVerified: false,
      serverId: eventData.serverId || '',
      serverName: eventData.serverName || eventData.serverId || '',
      serverUrl: eventData.serverUrl,
      isTemporaryLinkCall: eventData.isTemporaryLinkCall,
      timestamp,
      createdAt,
      connectedAt,
      endedAt,
      reason: eventData.reason,
      lastUpdatedAt,
      durationSeconds: eventData.durationSeconds,
      seen: isIncomingMissed ? serverSeen : true
    };

    set((state) => {
      const idx = state.events.findIndex((item) => item.callSessionId === nextEvent.callSessionId);
      if (idx === -1) {
        return { events: sortNewestFirst([...state.events, nextEvent]) };
      }

      const updated = [...state.events];
      const prev = updated[idx];
      updated[idx] = {
        ...prev,
        ...nextEvent,
        id: prev.id,
        remotePublicKey: nextEvent.remotePublicKey ?? prev.remotePublicKey,
        seen: prev.seen === true || serverSeen ? true : nextEvent.seen
      };
      return { events: sortNewestFirst(updated) };
    });

    await upsertCallEventBySessionId(nextEvent);
    window.dispatchEvent(new Event('call:history:updated'));
  },

  updateByCallSessionId: async (callSessionId, updates) => {
    if (!callSessionId) return;

    let nextEvents: CallEvent[] | null = null;
    set((state) => {
      const idx = state.events.findIndex((event) => event.callSessionId === callSessionId);
      if (idx === -1) return state;

      const updated = [...state.events];
      updated[idx] = {
        ...updated[idx],
        ...updates
      };
      nextEvents = updated;
      return { events: updated };
    });

    if (!nextEvents) return;
    await updateCallEventByCallSessionId(callSessionId, updates);
    window.dispatchEvent(new Event('call:history:updated'));
  },

  markMissedCallsSeen: async (filters) => {
    const seenAt = Date.now();
    let changedCallSessionIds: string[] = [];
    let nativeClearCallSessionIds: string[] = [];
    let nativeClearCallerIdentityIds: string[] = [];
    let changedPeerIdentityId = '';
    const changedByLocalIdentityId = new Map<string, Set<string>>();
    set((state) => {
      const effectiveServerId = (filters?.serverId || '').trim();
      const effectiveIdentityId = (filters?.identityId || '').trim();
      const effectivePeerIdentityId = (filters?.peerIdentityId || '').trim();
      changedPeerIdentityId = effectivePeerIdentityId;
      const changed = new Set<string>();
      const nativeClear = new Set<string>();
      const nativeClearCallers = new Set<string>();

      const next = state.events.map((event) => {
        const isIncomingMissed = isIncomingMissedEvent(event);
        if (!isIncomingMissed) return event;
        if (effectiveServerId && (event.serverId || '').trim() !== effectiveServerId) return event;
        if (effectiveIdentityId && event.localIdentityId && (event.localIdentityId || '').trim() !== effectiveIdentityId) return event;
        if (effectivePeerIdentityId && (event.remoteIdentityId || '').trim() !== effectivePeerIdentityId) return event;
        if (event.callSessionId) nativeClear.add(event.callSessionId);
        const remoteIdentityId = (event.remoteIdentityId || '').trim();
        if (remoteIdentityId) nativeClearCallers.add(remoteIdentityId);
        if (event.seen === true) return event;
        if (event.callSessionId) {
          changed.add(event.callSessionId);
          const localIdentityId = (event.localIdentityId || '').trim();
          if (localIdentityId) {
            const bucket = changedByLocalIdentityId.get(localIdentityId) || new Set<string>();
            bucket.add(event.callSessionId);
            changedByLocalIdentityId.set(localIdentityId, bucket);
          }
        }
        return { ...event, seen: true };
      });

      changedCallSessionIds = Array.from(changed);
      nativeClearCallSessionIds = Array.from(nativeClear);
      nativeClearCallerIdentityIds = Array.from(nativeClearCallers);
      return { events: next };
    });

    clearNativeMissedCallNotifications(nativeClearCallSessionIds);
    clearNativeMissedCallerBadges(nativeClearCallerIdentityIds);
    await markMissedCallEventsSeen(filters);
    if (changedCallSessionIds.length > 0 || changedPeerIdentityId) {
      const { sendMissedCallsSeen } = await import('../services/callHistorySync');
      const originalDeviceId = useAuthStore.getState().currentDeviceId;
      const syncTargets = changedByLocalIdentityId.size > 0
        ? Array.from(changedByLocalIdentityId.entries()).map(([localIdentityId, ids]) => ({
            localIdentityId,
            callSessionIds: Array.from(ids)
          }))
        : [{ localIdentityId: undefined, callSessionIds: changedCallSessionIds }];

      try {
        for (const target of syncTargets) {
          await sendMissedCallsSeen({
            localIdentityId: target.localIdentityId,
            callSessionIds: target.callSessionIds,
            peerIdentityId: changedPeerIdentityId,
            seenAt
          }).catch(() => {});
        }
      } finally {
        if (originalDeviceId && useAuthStore.getState().currentDeviceId !== originalDeviceId) {
          useAuthStore.getState().setCurrentDevice(originalDeviceId);
        }
      }
    }
  },

  clearHistory: async () => {
    set({ events: [] });
    await clearCallHistoryStorage();
  }
}));
